import { supabase } from '../lib/supabase';
import onetApiService from './onetApiService';
import blsApiService from './blsApiService';

/**
 * Comprehensive occupation service for CareerLift
 * Handles occupation search, filtering, and management with tier-based access
 */

class OccupationService {
  // Cache for categories to reduce API calls
  static categoriesCache = null;
  static cacheTimestamp = null;
  static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  // Ephemeral in-memory cache for BLS salaries by SOC code (session-only)
  static blsSalaryCache = new Map();
  // Cache for occupation lists (memory-only, session scoped)
  static occupationsCache = null;
  static occupationsCacheTimestamp = null;
  static OCCUPATIONS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  /**
   * Get all occupation categories with caching
   */
  static async getCategories() {
    try {
      const now = Date.now();
      
      // Return cached data if still valid
      if (this.categoriesCache && this.cacheTimestamp && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
        return { data: this.categoriesCache, error: null };
      }

      const { data, error } = await supabase?.from('occupation_categories')?.select('*')?.eq('is_active', true)?.order('sort_order', { ascending: true });

      if (error) {
        console.error('OccupationService: Error fetching occupation categories:', error);
        return { data: null, error };
      }

      // Update cache
      this.categoriesCache = data;
      this.cacheTimestamp = now;

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in getCategories:', error);
      return { data: null, error };
    }
  }

  /**
   * Prefetch the occupation list in the background after login
   */
  static async prefetchOccupations(userRole = 'free') {
    try {
      const now = Date.now();
      if (this.occupationsCache && this.occupationsCacheTimestamp && (now - this.occupationsCacheTimestamp) < this.OCCUPATIONS_CACHE_DURATION) {
        return; // still warm
      }
      const { data } = await this.getLocalDatabaseResults({ userRole, limit: 1000 });
      if (Array.isArray(data)) {
        this.occupationsCache = data;
        this.occupationsCacheTimestamp = now;
      }
    } catch (e) {
      // silent; this is best-effort
      console.debug('prefetchOccupations skipped:', e?.message || e);
    }
  }

  static getCachedOccupations() {
    const now = Date.now();
    if (this.occupationsCache && this.occupationsCacheTimestamp && (now - this.occupationsCacheTimestamp) < this.OCCUPATIONS_CACHE_DURATION) {
      return this.occupationsCache;
    }
    return null;
  }

  /**
   * Search O*NET occupations via API
   */
  static async searchOnetOccupations({
    search = null,
    searchQuery = '',
    categoryId = null,
    userRole = 'free',
    limit = 20
  } = {}) {
    try {
      const searchTerm = search || searchQuery;
      console.log('Searching O*NET occupations with params:', { searchTerm, categoryId, userRole, limit });
      
      // Search O*NET API
      const onetResult = await onetApiService.searchOccupations(searchTerm || 'all', limit);
      
      if (!onetResult.success) {
        console.error('O*NET API search failed:', onetResult.error);
        return { data: [], error: onetResult.error };
      }
      
      // Transform O*NET data to match our local format
      // O*NET returns data in response.data.career array
      const onetCareers = onetResult.data?.career || [];
      console.log('Raw O*NET careers data:', onetCareers.slice(0, 2)); // Log first 2 for debugging
      
      // Fetch detailed descriptions for each occupation
      const transformedData = await Promise.all(onetCareers.map(async (occupation) => {
        console.log('Processing O*NET occupation:', {
          title: occupation.title,
          code: occupation.code,
          category: occupation.category
        });
        
        // Get salary estimate immediately for O*NET occupations
        const salaryEstimate = this.estimateSalaryFromSocCode(occupation.code);
        
        // Map category with debugging
        const mappedCategoryId = this.mapOnetCategoryToLocal(occupation.category_id, occupation.code);
        console.log('Category mapping:', {
          title: occupation.title,
          onetCategory: occupation.category_id,
          socCode: occupation.code,
          mappedCategoryId: mappedCategoryId
        });
        
        // Try to get detailed description from O*NET API
        let description = 'O*NET occupation - detailed description available';
        try {
          const detailsResult = await onetApiService.getOccupationDetails(occupation.code, 'details');
          console.log('Details API response for', occupation.title, ':', {
            success: detailsResult.success,
            hasData: !!detailsResult.data,
            dataKeys: detailsResult.data ? Object.keys(detailsResult.data) : [],
            fullData: detailsResult.data, // Log the full response to see structure
            careerData: detailsResult.data?.career,
            description: detailsResult.data?.career?.description
          });
          
          if (detailsResult.success && detailsResult.data?.career?.description) {
            description = detailsResult.data.career.description;
            console.log('✅ Got detailed description for', occupation.title, ':', description.substring(0, 100) + '...');
          } else if (detailsResult.success && detailsResult.data?.description) {
            // Try alternative response structure
            description = detailsResult.data.description;
            console.log('✅ Got description from alternative structure for', occupation.title, ':', description.substring(0, 100) + '...');
          } else {
            // Fallback: Generate a basic description based on the job title
            description = `Professional role in ${occupation.title.toLowerCase()}. This position involves specialized skills and knowledge in the field.`;
            console.log('❌ No description found for', occupation.title, '- using generated fallback');
          }
        } catch (error) {
          // Fallback: Generate a basic description based on the job title
          description = `Professional role in ${occupation.title.toLowerCase()}. This position involves specialized skills and knowledge in the field.`;
          console.log('❌ Could not fetch detailed description for', occupation.title, ':', error.message, '- using generated fallback');
        }
        
        return {
          id: occupation.code || `onet-${Math.random().toString(36).substr(2, 9)}`,
          title: occupation.title || 'Unknown Occupation',
          description: description,
          soc_code: occupation.code,
          category_id: mappedCategoryId,
          tier: userRole === 'free' ? 'free' : 'premium',
          is_active: true,
          source: 'onet',
          salary_range_min: salaryEstimate.min, // Set salary data immediately
          salary_range_max: salaryEstimate.max, // Set salary data immediately
          growth_outlook: occupation.tags?.bright_outlook ? 'growing' : 'stable',
          remote_friendly: false, // Default, can be enhanced later
          tags: Object.keys(occupation.tags || {}),
          industry_tags: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          onet_href: occupation.href // Store O*NET URL for detailed data
        };
      }));
      
      console.log('O*NET search returned:', transformedData.length, 'occupations');
      
      // Debug: Log sample O*NET occupations with SOC codes
      if (transformedData.length > 0) {
        console.log('Sample O*NET occupations:', transformedData.slice(0, 3).map(occ => ({
          title: occ.title,
          soc_code: occ.soc_code,
          salary_range_min: occ.salary_range_min,
          salary_range_max: occ.salary_range_max
        })));
      }
      
      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error searching O*NET occupations:', error);
      return { data: [], error };
    }
  }

  /**
   * Get comprehensive occupation data combining O*NET and BLS data
   */
  static async getComprehensiveOccupationData(socCode, stateCode = null) {
    try {
      console.log('Getting comprehensive data for SOC code:', socCode);
      
      // Get O*NET data
      const onetResult = await onetApiService.getOccupationDetails(socCode);
      
      // Get BLS data
      const blsResult = await blsApiService.getComprehensiveOccupationData(socCode, stateCode);
      
      // Combine the data
      const comprehensiveData = {
        soc_code: socCode,
        state_code: stateCode,
        onet_data: onetResult.success ? onetResult.data : null,
        bls_data: blsResult.success ? blsResult.data : null,
        last_updated: new Date().toISOString(),
        sources: {
          onet: onetResult.success,
          bls: blsResult.success
        }
      };
      
      return { success: true, data: comprehensiveData };
    } catch (error) {
      console.error('Error getting comprehensive occupation data:', error);
      return { success: false, error };
    }
  }

  /**
   * Search occupations with O*NET + BLS data integration
   */
  static async searchOccupationsWithBlsData({
    search = null,
    searchQuery = '',
    categoryId = null,
    userRole = 'free',
    limit = 20,
    includeBlsData = false
  } = {}) {
    try {
      // First get O*NET occupations
      const onetResult = await this.searchOnetOccupations({
        search,
        searchQuery,
        categoryId,
        userRole,
        limit: limit || (userRole === 'admin' ? 1000 : 100) // Admin gets full O*NET dataset (1000+ total)
      });
      
      if (!onetResult.success || !onetResult.data.length) {
        return onetResult;
      }
      
      // If BLS data is requested, enhance each occupation
      if (includeBlsData && (userRole === 'premium' || userRole === 'admin')) {
        console.log('Enhancing occupations with BLS data...');
        
        const enhancedOccupations = await Promise.all(
          onetResult.data.map(async (occupation) => {
            if (occupation.soc_code) {
              try {
                console.log(`Fetching BLS data for ${occupation.title} (SOC: ${occupation.soc_code})`);
                const blsData = await blsApiService.getComprehensiveOccupationData(occupation.soc_code);
                console.log(`BLS data for ${occupation.title}:`, blsData);
                
                if (blsData.success) {
                  // Extract salary data from BLS response
                  const salaryData = this.extractSalaryFromBlsData(blsData.data);
                  console.log(`Extracted salary data for ${occupation.title}:`, salaryData);
                  
                  return {
                    ...occupation,
                    salary_range_min: salaryData.min || occupation.salary_range_min,
                    salary_range_max: salaryData.max || occupation.salary_range_max,
                    bls_data: blsData.data,
                    enhanced_with_bls: true
                  };
                } else {
                  console.warn(`BLS data failed for ${occupation.title}:`, blsData.error);
                }
              } catch (error) {
                console.warn(`Failed to get BLS data for ${occupation.soc_code}:`, error);
              }
            }
            return occupation;
          })
        );
        
        return { data: enhancedOccupations, error: null };
      }
      
      // Always add salary estimates for O*NET occupations (fallback for when BLS fails)
      console.log(`Adding estimated salary data for O*NET occupations...`);
      
      const enhancedOccupations = onetResult.data.map(occupation => {
        // Add estimated salary ranges based on SOC code patterns
        const estimatedSalary = this.estimateSalaryFromSocCode(occupation.soc_code);
        console.log(`Estimated salary for ${occupation.title} (SOC: ${occupation.soc_code}):`, estimatedSalary);
        
        return {
          ...occupation,
          salary_range_min: estimatedSalary.min,
          salary_range_max: estimatedSalary.max,
          salary_source: 'estimated_onet'
        };
      });
      
      return { data: enhancedOccupations, error: null };
    } catch (error) {
      console.error('Error searching occupations with BLS data:', error);
      return { data: [], error };
    }
  }

  /**
   * Get results from local database
   */
  static async getLocalDatabaseResults({
    searchTerm = null,
    categoryId = null,
    userRole = 'free',
    limit = 1000
  }) {
    try {
      console.log('Getting local database results...');
      
      // Build query with direct table access
      let query = supabase?.from('occupations')?.select(`
        *,
        category:occupation_categories(name, slug, icon, color_class, bg_color_class)
      `)?.eq('is_active', true);

      // Apply search filter
      if (searchTerm) {
        query = query?.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`);
      }

      // Apply category filter
      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      // Apply tier filter based on user role
      if (userRole === 'free') {
        query = query?.eq('tier', 'free');
      }
      // For premium/admin users, get all tiers (no filter)

      // Apply limit
      if (limit) {
        query = query?.limit(limit);
      }

      // Order alphabetically
      query = query?.order('title', { ascending: true });

      console.log('Local database query params:', {
        searchTerm,
        categoryId,
        userRole,
        limit,
        tierFilter: userRole === 'free' ? 'free' : 'all'
      });

      let { data, error } = await query;

      if (error) {
        console.error('Error getting local database results:', error);
        return { data: [], error };
      }

      console.log('Local database results:', data?.length || 0);
      // Enrich missing salaries with live BLS where possible
      data = await this.enrichOccupationsWithBlsSalaries(data);
      
      // Debug: Log sample occupation data to check salary ranges
      if (data && data.length > 0) {
        const sampleWithSalary = data.find(occ => occ.salary_range_min && occ.salary_range_max);
        if (sampleWithSalary) {
          console.log('Sample occupation with salary data:', {
            title: sampleWithSalary.title,
            salary_range_min: sampleWithSalary.salary_range_min,
            salary_range_max: sampleWithSalary.salary_range_max
          });
        } else {
          console.log('No occupations with salary data found. Sample occupation:', {
            title: data[0].title,
            salary_range_min: data[0].salary_range_min,
            salary_range_max: data[0].salary_range_max
          });
        }
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error in getLocalDatabaseResults:', error);
      return { data: [], error };
    }
  }

  /**
   * Enrich occupations with live BLS salary ranges when missing
   * Uses in-memory cache; no localStorage, no DB writes
   */
  static async enrichOccupationsWithBlsSalaries(occupations) {
    try {
      if (!Array.isArray(occupations) || occupations.length === 0) return occupations;

      // Determine which SOC codes need fetching
      const missing = occupations.filter(o => (!o?.salary_range_min || !o?.salary_range_max) && !!o?.soc_code);
      const socCodesNeeded = Array.from(new Set(missing.map(o => o.soc_code)))
        .filter(code => !this.blsSalaryCache.has(code))
        .slice(0, 12); // Cap concurrent lookups to avoid rate limits

      if (socCodesNeeded.length > 0) {
        console.log('Fetching live BLS salaries for SOC codes:', socCodesNeeded);
        await Promise.all(socCodesNeeded.map(async (soc) => {
          try {
            const bls = await blsApiService.getComprehensiveOccupationData(soc);
            if (bls?.success) {
              const salary = this.extractSalaryFromBlsData(bls.data);
              if (salary?.min || salary?.max) {
                this.blsSalaryCache.set(soc, { min: salary.min || null, max: salary.max || null, fetchedAt: Date.now() });
              }
            }
          } catch (e) {
            console.debug('BLS fetch failed for', soc, e?.message || e);
          }
        }));
      }

      // Apply cached salaries to occupations missing values
      const enriched = occupations.map(o => {
        if ((!o?.salary_range_min || !o?.salary_range_max) && o?.soc_code && this.blsSalaryCache.has(o.soc_code)) {
          const cached = this.blsSalaryCache.get(o.soc_code);
          return {
            ...o,
            salary_range_min: o?.salary_range_min || cached?.min || null,
            salary_range_max: o?.salary_range_max || cached?.max || null,
            salary_source: 'bls_live'
          };
        }
        return o;
      });

      return enriched;
    } catch (e) {
      console.debug('BLS enrichment skipped due to error:', e?.message || e);
      return occupations;
    }
  }

  /**
   * Combine local database and O*NET data
   */
  /**
   * Get O*NET data with proper server-side categorization
   */
  static async getOnetDataWithProperCategorization({
    searchTerm,
    categoryId,
    userRole,
    limit,
    includeBlsData
  }) {
    try {
      // Get raw O*NET data
      const onetResult = await this.searchOccupationsWithBlsData({
        search: searchTerm,
        categoryId: null, // Get all O*NET data first
        userRole,
        limit,
        includeBlsData
      });

      console.log('O*NET data fetch result:', {
        success: onetResult.success,
        dataLength: onetResult.data?.length || 0,
        limit: limit
      });

      if (!onetResult.success || !onetResult.data.length) {
        return { data: [], error: null };
      }

      // Categorize each O*NET occupation properly at the source
      const properlyCategorizedData = onetResult.data.map(occupation => {
        const categoryMapping = this.mapOnetCategoryBySocCode(occupation.soc_code, occupation.title);
        
        return {
          ...occupation,
          category_id: categoryMapping.id,
          category_name: categoryMapping.name,
          category: {
            name: categoryMapping.name,
            slug: categoryMapping.slug,
            icon: categoryMapping.icon,
            color_class: categoryMapping.color_class,
            bg_color_class: categoryMapping.bg_color_class
          },
          source: 'onet_properly_categorized'
        };
      });

      console.log('O*NET categorization result:', {
        originalCount: onetResult.data.length,
        categorizedCount: properlyCategorizedData.length,
        sampleCategorized: properlyCategorizedData.slice(0, 3).map(o => ({
          title: o.title,
          category_id: o.category_id,
          category_name: o.category_name
        }))
      });

      // Apply category filter at the source if specified
      let filteredData = properlyCategorizedData;
      if (categoryId) {
        filteredData = properlyCategorizedData.filter(occupation => 
          occupation.category_id === categoryId
        );
      }

      // Apply search filter at the source if specified
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredData = filteredData.filter(occupation =>
          occupation.title?.toLowerCase().includes(searchLower) ||
          occupation.description?.toLowerCase().includes(searchLower) ||
          occupation.tags?.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }

      console.log('O*NET final filtered result:', {
        categorizedCount: properlyCategorizedData.length,
        finalCount: filteredData.length,
        categoryId,
        searchTerm,
        hasCategoryFilter: !!categoryId,
        hasSearchFilter: !!searchTerm
      });

      return { data: filteredData, error: null };
    } catch (error) {
      console.error('Error getting properly categorized O*NET data:', error);
      return { data: [], error: error.message };
    }
  }

  /**
   * Combine data with proper deduplication (no more flawed mapping)
   */
  static combineDataWithDeduplication(localData, onetData) {
    console.log('Combining data with proper deduplication...');
    
    // Start with local data (already properly categorized)
    const combined = [...localData];
    
    // Add O*NET data that doesn't already exist (by title and SOC code)
    const localTitles = new Set(localData.map(occ => occ.title?.toLowerCase()));
    const localSocCodes = new Set(localData.map(occ => occ.soc_code).filter(Boolean));
    
    const uniqueOnetData = onetData.filter(onetOcc => {
      const titleExists = localTitles.has(onetOcc.title?.toLowerCase());
      const socCodeExists = onetOcc.soc_code && localSocCodes.has(onetOcc.soc_code);
      return !titleExists && !socCodeExists;
    });
    
    const result = [...combined, ...uniqueOnetData];
    console.log('Deduplicated results:', {
      local: localData.length,
      onet: onetData.length,
      uniqueOnet: uniqueOnetData.length,
      total: result.length
    });
    
    return result;
  }

  static combineLocalAndOnetData(localData, onetData) {
    console.log('Combining local and O*NET data...');
    
    // Start with local data (has proper categories)
    const combined = [...localData];
    
    // Add O*NET data that doesn't already exist in local database
    const localTitles = new Set(localData.map(occ => occ.title?.toLowerCase()));
    const localSocCodes = new Set(localData.map(occ => occ.soc_code).filter(Boolean));
    
    const uniqueOnetData = onetData.filter(onetOcc => {
      const titleExists = localTitles.has(onetOcc.title?.toLowerCase());
      const socCodeExists = onetOcc.soc_code && localSocCodes.has(onetOcc.soc_code);
      return !titleExists && !socCodeExists;
    });
    
    // Add unique O*NET occupations with proper SOC code-based category mapping
    const enhancedOnetData = uniqueOnetData.map(onetOcc => {
      // Map O*NET occupations to appropriate categories based on SOC codes (same logic as database fix)
      const categoryMapping = this.mapOnetCategoryBySocCode(onetOcc.soc_code, onetOcc.title);
      
      return {
        ...onetOcc,
        category_id: categoryMapping.id,
        category_name: categoryMapping.name,
        category: {
          name: categoryMapping.name,
          slug: categoryMapping.slug,
          icon: categoryMapping.icon,
          color_class: categoryMapping.color_class,
          bg_color_class: categoryMapping.bg_color_class
        },
        source: 'onet_enhanced'
      };
    });
    
    const result = [...combined, ...enhancedOnetData];
    console.log('Combined data:', {
      local: localData.length,
      onet: onetData.length,
      uniqueOnet: uniqueOnetData.length,
      total: result.length
    });
    
    return result;
  }

  /**
   * Estimate salary range based on SOC code patterns
   */
  static estimateSalaryFromSocCode(socCode) {
    if (!socCode) {
      return { min: 30000, max: 50000 };
    }
    
    // Extract major group from SOC code (first two digits)
    const majorGroup = parseInt(socCode.split('-')[0]);
    
    // Salary estimates based on SOC major groups
    const salaryRanges = {
      11: { min: 60000, max: 150000 }, // Management
      13: { min: 45000, max: 120000 }, // Business and Financial Operations
      15: { min: 50000, max: 130000 }, // Computer and Mathematical
      17: { min: 55000, max: 140000 }, // Architecture and Engineering
      19: { min: 40000, max: 100000 }, // Life, Physical, and Social Science
      21: { min: 35000, max: 90000 },  // Community and Social Service
      23: { min: 40000, max: 110000 }, // Legal
      25: { min: 35000, max: 80000 },  // Education, Training, and Library
      27: { min: 30000, max: 80000 },  // Arts, Design, Entertainment, Sports, and Media
      29: { min: 40000, max: 100000 }, // Healthcare Practitioners and Technical
      31: { min: 25000, max: 60000 },  // Healthcare Support
      33: { min: 30000, max: 70000 },  // Protective Service
      35: { min: 25000, max: 50000 },  // Food Preparation and Serving Related
      37: { min: 25000, max: 50000 },  // Building and Grounds Cleaning and Maintenance
      39: { min: 25000, max: 50000 },  // Personal Care and Service
      41: { min: 30000, max: 80000 },  // Sales and Related
      43: { min: 25000, max: 60000 },  // Office and Administrative Support
      45: { min: 25000, max: 50000 },  // Farming, Fishing, and Forestry
      47: { min: 30000, max: 70000 },  // Construction and Extraction
      49: { min: 35000, max: 80000 },  // Installation, Maintenance, and Repair
      51: { min: 30000, max: 70000 },  // Production
      53: { min: 30000, max: 80000 },  // Transportation and Material Moving
      55: { min: 40000, max: 100000 }  // Military Specific
    };
    
    return salaryRanges[majorGroup] || { min: 30000, max: 60000 };
  }

  /**
   * Extract salary data from BLS API response
   */
  static extractSalaryFromBlsData(blsData) {
    try {
      // BLS data structure may vary, try to extract wage information
      const wages = blsData?.wages?.data || blsData?.wages || {};
      const employment = blsData?.employment?.data || blsData?.employment || {};
      
      // Look for wage data in various possible locations
      let minSalary = null;
      let maxSalary = null;
      
      // Try to extract from wages data
      if (wages?.Results?.series?.[0]?.data?.[0]?.value) {
        const wageValue = parseFloat(wages.Results.series[0].data[0].value);
        if (!isNaN(wageValue)) {
          // BLS data is often annual, convert to appropriate range
          minSalary = Math.round(wageValue * 0.8); // 80% of median
          maxSalary = Math.round(wageValue * 1.2); // 120% of median
        }
      }
      
      // Fallback: try to extract from employment data
      if (!minSalary && employment?.Results?.series?.[0]?.data?.[0]?.value) {
        const empValue = parseFloat(employment.Results.series[0].data[0].value);
        if (!isNaN(empValue)) {
          // Use employment data as proxy for salary estimation
          minSalary = Math.round(empValue * 0.8);
          maxSalary = Math.round(empValue * 1.2);
        }
      }
      
      return {
        min: minSalary,
        max: maxSalary
      };
    } catch (error) {
      console.warn('Error extracting salary from BLS data:', error);
      return { min: null, max: null };
    }
  }

  /**
   * Map O*NET occupation to appropriate local category based on SOC code (same logic as database fix)
   */
  static mapOnetCategoryBySocCode(socCode, title) {
    if (!socCode) {
      // Fallback to title-based mapping if no SOC code
      return this.mapOnetOccupationToCategory(title);
    }

    const socMajorGroup = socCode.substring(0, 2);
    const titleLower = title?.toLowerCase() || '';

    // Healthcare & Medical (SOC 29-xxxx, 31-xxxx)
    if (socMajorGroup === '29' || socMajorGroup === '31') {
      return {
        id: '550e8400-e29b-41d4-a716-446655440002',
        name: 'Healthcare & Medical',
        slug: 'healthcare-medical',
        icon: 'Heart',
        color_class: 'text-red-600',
        bg_color_class: 'bg-red-50'
      };
    }

    // Engineering & Architecture (SOC 17-xxxx)
    if (socMajorGroup === '17') {
      return {
        id: '9412465c-4ad1-452d-bc7c-9e0ed615992d',
        name: 'Engineering & Technical',
        slug: 'engineering-technical',
        icon: 'Wrench',
        color_class: 'text-orange-600',
        bg_color_class: 'bg-orange-50'
      };
    }

    // Technology & IT (SOC 15-xxxx)
    if (socMajorGroup === '15') {
      return {
        id: '550e8400-e29b-41d4-a716-446655440003',
        name: 'Technology & IT',
        slug: 'technology-it',
        icon: 'Monitor',
        color_class: 'text-blue-600',
        bg_color_class: 'bg-blue-50'
      };
    }

    // Business & Management (SOC 11-xxxx, 13-xxxx)
    if (socMajorGroup === '11' || socMajorGroup === '13') {
      // Special handling for financial jobs
      if (titleLower.includes('financial') || titleLower.includes('accounting') || titleLower.includes('auditor') || titleLower.includes('bookkeeper')) {
        return {
          id: 'e84418ca-4b4d-4bd0-8d63-2496ba0e53b7',
          name: 'Finance & Accounting',
          slug: 'finance-accounting',
          icon: 'DollarSign',
          color_class: 'text-green-600',
          bg_color_class: 'bg-green-50'
        };
      }
      return {
        id: '550e8400-e29b-41d4-a716-446655440004',
        name: 'Business & Management',
        slug: 'business-management',
        icon: 'Briefcase',
        color_class: 'text-purple-600',
        bg_color_class: 'bg-purple-50'
      };
    }

    // Sales & Marketing (SOC 41-xxxx)
    if (socMajorGroup === '41') {
      return {
        id: 'e84418ca-4b4d-4bd0-8d63-2496ba0e53b7',
        name: 'Sales & Marketing',
        slug: 'sales-marketing',
        icon: 'TrendingUp',
        color_class: 'text-pink-600',
        bg_color_class: 'bg-pink-50'
      };
    }

    // Education & Training (SOC 25-xxxx)
    if (socMajorGroup === '25') {
      // Special handling for school healthcare jobs
      if (titleLower.includes('school') && (titleLower.includes('nurse') || titleLower.includes('psychologist') || titleLower.includes('psychiatrist'))) {
        return {
          id: '550e8400-e29b-41d4-a716-446655440002',
          name: 'Healthcare & Medical',
          slug: 'healthcare-medical',
          icon: 'Heart',
          color_class: 'text-red-600',
          bg_color_class: 'bg-red-50'
        };
      }
      return {
        id: '0e6aa89e-8990-484a-908e-d2c737703994',
        name: 'Education & Training',
        slug: 'education-training',
        icon: 'BookOpen',
        color_class: 'text-green-600',
        bg_color_class: 'bg-green-50'
      };
    }

    // Creative & Design (SOC 27-xxxx)
    if (socMajorGroup === '27') {
      return {
        id: '550e8400-e29b-41d4-a716-446655440005',
        name: 'Creative & Design',
        slug: 'creative-design',
        icon: 'Palette',
        color_class: 'text-purple-600',
        bg_color_class: 'bg-purple-50'
      };
    }

    // Trades & Technical (SOC 47-xxxx, 49-xxxx)
    if (socMajorGroup === '47' || socMajorGroup === '49') {
      return {
        id: '550e8400-e29b-41d4-a716-446655440001',
        name: 'Trades & Technical',
        slug: 'trades-technical',
        icon: 'Wrench',
        color_class: 'text-orange-600',
        bg_color_class: 'bg-orange-50'
      };
    }

    // Service & Hospitality (SOC 35-xxxx, 37-xxxx, 39-xxxx)
    if (socMajorGroup === '35' || socMajorGroup === '37' || socMajorGroup === '39') {
      return {
        id: '06790e77-ebc4-4e41-86b8-e212fed5456c',
        name: 'Service & Hospitality',
        slug: 'service-hospitality',
        icon: 'Users',
        color_class: 'text-indigo-600',
        bg_color_class: 'bg-indigo-50'
      };
    }

    // Default fallback to title-based mapping
    return this.mapOnetOccupationToCategory(title);
  }

  /**
   * Map O*NET occupation title to appropriate local category (fallback method)
   */
  static mapOnetOccupationToCategory(title) {
    const titleLower = title?.toLowerCase() || '';
    
    // Healthcare & Medical
    if (titleLower.includes('nurse') || titleLower.includes('doctor') || titleLower.includes('physician') || 
        titleLower.includes('medical') || titleLower.includes('health') || titleLower.includes('therapist') ||
        titleLower.includes('pharmacist') || titleLower.includes('dentist') || titleLower.includes('veterinarian') ||
        titleLower.includes('neuropsychologist') || titleLower.includes('psychologist') || titleLower.includes('psychiatrist') ||
        titleLower.includes('clinical') || titleLower.includes('neuro')) {
      return {
        id: '550e8400-e29b-41d4-a716-446655440002',
        name: 'Healthcare & Medical',
        slug: 'healthcare-medical',
        icon: 'Heart',
        color_class: 'text-red-600',
        bg_color_class: 'bg-red-50'
      };
    }
    
    // Technology & IT
    if (titleLower.includes('software') || titleLower.includes('developer') || titleLower.includes('programmer') ||
        titleLower.includes('engineer') || titleLower.includes('analyst') || titleLower.includes('data') ||
        titleLower.includes('cyber') || titleLower.includes('security') || titleLower.includes('network') ||
        titleLower.includes('system') || titleLower.includes('database') || titleLower.includes('web')) {
      return {
        id: '550e8400-e29b-41d4-a716-446655440003',
        name: 'Technology & IT',
        slug: 'technology-it',
        icon: 'Monitor',
        color_class: 'text-blue-600',
        bg_color_class: 'bg-blue-50'
      };
    }
    
    // Business & Management
    if (titleLower.includes('manager') || titleLower.includes('director') || titleLower.includes('executive') ||
        titleLower.includes('supervisor') || titleLower.includes('coordinator') || titleLower.includes('administrator') ||
        titleLower.includes('consultant') || titleLower.includes('analyst') || titleLower.includes('specialist')) {
      return {
        id: '550e8400-e29b-41d4-a716-446655440004',
        name: 'Business & Management',
        slug: 'business-management',
        icon: 'Briefcase',
        color_class: 'text-purple-600',
        bg_color_class: 'bg-purple-50'
      };
    }
    
    // Education & Training
    if (titleLower.includes('teacher') || titleLower.includes('professor') || titleLower.includes('instructor') ||
        titleLower.includes('educator') || titleLower.includes('trainer') || titleLower.includes('counselor') ||
        titleLower.includes('librarian') || titleLower.includes('academic')) {
      return {
        id: '0e6aa89e-8990-484a-908e-d2c737703994',
        name: 'Education & Training',
        slug: 'education-training',
        icon: 'BookOpen',
        color_class: 'text-green-600',
        bg_color_class: 'bg-green-50'
      };
    }
    
    // Creative & Design
    if (titleLower.includes('artist') || titleLower.includes('designer') || titleLower.includes('creative') ||
        titleLower.includes('writer') || titleLower.includes('editor') || titleLower.includes('photographer') ||
        titleLower.includes('musician') || titleLower.includes('actor') || titleLower.includes('producer')) {
      return {
        id: '550e8400-e29b-41d4-a716-446655440005',
        name: 'Creative & Design',
        slug: 'creative-design',
        icon: 'Palette',
        color_class: 'text-pink-600',
        bg_color_class: 'bg-pink-50'
      };
    }
    
    // Engineering & Technical
    if (titleLower.includes('engineer') || titleLower.includes('technician') || titleLower.includes('technologist') ||
        titleLower.includes('architect') || titleLower.includes('scientist') || titleLower.includes('researcher') ||
        titleLower.includes('laboratory') || titleLower.includes('quality') || titleLower.includes('testing') ||
        titleLower.includes('aviation') || titleLower.includes('aircraft') || titleLower.includes('pilot') ||
        titleLower.includes('air traffic') || titleLower.includes('aerospace')) {
      return {
        id: '9412465c-4ad1-452d-bc7c-9e0ed615992d',
        name: 'Engineering & Technical',
        slug: 'engineering-technical',
        icon: 'Wrench',
        color_class: 'text-orange-600',
        bg_color_class: 'bg-orange-50'
      };
    }
    
    // Sales & Marketing
    if (titleLower.includes('sales') || titleLower.includes('marketing') || titleLower.includes('advertising') ||
        titleLower.includes('promotion') || titleLower.includes('representative') || titleLower.includes('agent') ||
        titleLower.includes('broker') || titleLower.includes('retail')) {
      return {
        id: 'e84418ca-4b4d-4bd0-8d63-2496ba0e53b7',
        name: 'Sales & Marketing',
        slug: 'sales-marketing',
        icon: 'TrendingUp',
        color_class: 'text-yellow-600',
        bg_color_class: 'bg-yellow-50'
      };
    }
    
    // Service & Hospitality
    if (titleLower.includes('service') || titleLower.includes('hospitality') || titleLower.includes('food') ||
        titleLower.includes('restaurant') || titleLower.includes('hotel') || titleLower.includes('tourism') ||
        titleLower.includes('customer') || titleLower.includes('receptionist') || titleLower.includes('waiter')) {
      return {
        id: '06790e77-ebc4-4e41-86b8-e212fed5456c',
        name: 'Service & Hospitality',
        slug: 'service-hospitality',
        icon: 'Users',
        color_class: 'text-indigo-600',
        bg_color_class: 'bg-indigo-50'
      };
    }
    
    // Business & Finance
    if (titleLower.includes('finance') || titleLower.includes('accounting') || titleLower.includes('banking') ||
        titleLower.includes('financial') || titleLower.includes('auditor') || titleLower.includes('accountant') ||
        titleLower.includes('investment') || titleLower.includes('insurance')) {
      return {
        id: '642c6bf2-5ddb-4f2e-a047-159d1c590543',
        name: 'Business & Finance',
        slug: 'business-finance',
        icon: 'DollarSign',
        color_class: 'text-emerald-600',
        bg_color_class: 'bg-emerald-50'
      };
    }
    
    // Default to Trades & Technical for everything else
    return {
      id: '550e8400-e29b-41d4-a716-446655440001',
      name: 'Trades & Technical',
      slug: 'trades-technical',
      icon: 'Tool',
      color_class: 'text-gray-600',
      bg_color_class: 'bg-gray-50'
    };
  }

  /**
   * Map O*NET category to local category ID
   */
  static mapOnetCategoryToLocal(onetCategory, socCode = null) {
    console.log('mapOnetCategoryToLocal called with:', { onetCategory, socCode });
    
    // Enhanced mapping based on O*NET's actual categories and SOC codes
    const categoryMapping = {
      'Computer and Mathematical': '550e8400-e29b-41d4-a716-446655440003', // Technology & IT
      'Healthcare Practitioners and Technical': '550e8400-e29b-41d4-a716-446655440002', // Healthcare & Medical
      'Business and Financial Operations': '550e8400-e29b-41d4-a716-446655440004', // Business & Management
      'Management': '550e8400-e29b-41d4-a716-446655440004', // Business & Management
      'Education, Training, and Library': '0e6aa89e-8990-484a-908e-d2c737703994', // Education & Training
      'Arts, Design, Entertainment, Sports, and Media': '550e8400-e29b-41d4-a716-446655440005', // Creative & Design
      'Architecture and Engineering': '9412465c-4ad1-452d-bc7c-9e0ed615992d', // Engineering & Technical
      'Sales and Related': 'e84418ca-4b4d-4bd0-8d63-2496ba0e53b7', // Sales & Marketing
      'Office and Administrative Support': '550e8400-e29b-41d4-a716-446655440004', // Business & Management
      'Installation, Maintenance, and Repair': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
      'Production': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
      'Transportation and Material Moving': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
      'Food Preparation and Serving Related': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
      'Personal Care and Service': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
      'Protective Service': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
      'Building and Grounds Cleaning and Maintenance': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
      'Farming, Fishing, and Forestry': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
      'Construction and Extraction': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
      'Life, Physical, and Social Science': '550e8400-e29b-41d4-a716-446655440003', // Technology & IT
      'Community and Social Service': '0e6aa89e-8990-484a-908e-d2c737703994', // Education & Training
      'Legal': '550e8400-e29b-41d4-a716-446655440004', // Business & Management
      'Healthcare Support': '550e8400-e29b-41d4-a716-446655440002', // Healthcare & Medical
      'Military Specific': '550e8400-e29b-41d4-a716-446655440001' // Trades & Technical
    };
    
    // First try the category mapping
    if (categoryMapping[onetCategory]) {
      console.log('Found category mapping:', onetCategory, '->', categoryMapping[onetCategory]);
      return categoryMapping[onetCategory];
    }
    
    // If no category mapping, use SOC code as fallback
    if (socCode) {
      const socMajorGroup = socCode.substring(0, 2);
      console.log('Using SOC code fallback:', socCode, '-> major group:', socMajorGroup);
      
      const socCategoryMapping = {
        '11': '550e8400-e29b-41d4-a716-446655440004', // Business & Management
        '13': '550e8400-e29b-41d4-a716-446655440004', // Business & Management
        '15': '550e8400-e29b-41d4-a716-446655440003', // Technology & IT
        '17': '9412465c-4ad1-452d-bc7c-9e0ed615992d', // Engineering & Technical
        '25': '0e6aa89e-8990-484a-908e-d2c737703994', // Education & Training
        '27': '550e8400-e29b-41d4-a716-446655440005', // Creative & Design
        '29': '550e8400-e29b-41d4-a716-446655440002', // Healthcare & Medical
        '31': '550e8400-e29b-41d4-a716-446655440002', // Healthcare & Medical
        '33': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
        '35': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
        '37': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
        '39': '06790e77-ebc4-4e41-86b8-e212fed5456c', // Service & Hospitality
        '41': 'e84418ca-4b4d-4bd0-8d63-2496ba0e53b7', // Sales & Marketing
        '43': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
        '47': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
        '49': '550e8400-e29b-41d4-a716-446655440001', // Trades & Technical
        '51': '550e8400-e29b-41d4-a716-446655440001'  // Trades & Technical
      };
      
      if (socCategoryMapping[socMajorGroup]) {
        console.log('Found SOC mapping:', socMajorGroup, '->', socCategoryMapping[socMajorGroup]);
        return socCategoryMapping[socMajorGroup];
      }
    }
    
    console.log('Using default category: Trades & Technical');
    return '550e8400-e29b-41d4-a716-446655440001'; // Default to Trades & Technical
  }

  /**
   * Track category search for analytics
   */
  static async trackCategorySearch(categoryId, searchTerm, resultsCount, userRole, sessionId = null) {
    try {
      if (!categoryId) return;
      
      await supabase.rpc('track_category_search', {
        p_category_id: categoryId,
        p_search_term: searchTerm,
        p_results_count: resultsCount,
        p_user_role: userRole,
        p_session_id: sessionId
      });
    } catch (error) {
      // Silently fail to not break search functionality
      console.debug('Analytics tracking failed:', error);
    }
  }

  /**
   * Search occupations with advanced filtering and tier-based access
   * Now integrates both local database and O*NET API
   */
  static async searchOccupations({
    search = null,
    searchQuery = '',
    categoryId = null,
    tier = null,
    userRole = 'free',
    limit = null, // No default limit - get all results
    offset = 0,
    useOnet = false // New parameter to enable O*NET search
  } = {}) {
    try {
      // Support both 'search' and 'searchQuery' parameters for compatibility
      const searchTerm = search || searchQuery;
      console.log('Searching occupations with params:', { search, searchQuery, searchTerm, categoryId, tier, userRole, limit, useOnet });
      
      // When requested, combine local DB with properly categorized O*NET data and deduplicate
      if (useOnet) {
        console.log('Using combined Local + O*NET results with deduplication');

        // Local database
        const localResult = await this.getLocalDatabaseResults({
          searchTerm,
          categoryId,
          userRole,
          limit: limit || 1000
        });

        // Properly categorized O*NET data (with BLS enrichment when available)
        const onetResult = await this.getOnetDataWithProperCategorization({
          searchTerm,
          categoryId,
          userRole,
          limit: 1000, // Increased from 134 to get all O*NET occupations
          includeBlsData: userRole === 'premium' || userRole === 'admin'
        });

        const combined = this.combineDataWithDeduplication(localResult.data || [], onetResult.data || []);

        // Apply client-specified limit if provided
        const finalData = limit ? combined.slice(0, limit) : combined;
        
        console.log('Combined occupation data:', {
          localCount: localResult.data?.length || 0,
          onetCount: onetResult.data?.length || 0,
          combinedCount: combined.length,
          finalCount: finalData.length
        });
        
        return { data: finalData, error: null, total: finalData.length };
      }
      
      // Fall back to local database only
      console.log('Using local database only for occupation search');
      
      // Serve from cache when possible (no search/category filters)
      const cached = (!searchTerm && !categoryId) ? this.getCachedOccupations() : null;
      if (cached) {
        let data = cached;
        if (userRole === 'free') {
          data = data.filter(occ => occ?.tier === 'free');
          if (!limit) {
            // mimic free smart limit when viewing all
            data = data.slice(0, 72);
          }
        }
        if (limit) data = data.slice(0, limit);
        return { data, error: null, total: data.length };
      }

      // Build query with direct table access instead of RPC function
      let query = supabase?.from('occupations')?.select(`
        *,
        category:occupation_categories(name, slug, icon, color_class, bg_color_class)
      `)?.eq('is_active', true);

      // Apply search filter
      if (searchTerm) {
        query = query?.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`);
      }

      // Apply category filter
      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      // Apply tier filter based on user role
      if (userRole === 'free') {
        // For free users, show a curated selection of ~75 high-quality jobs
        // This creates a better experience and makes premium more compelling
        query = query?.eq('tier', 'free');
        
        // Apply smart limiting for free users
        if (!limit) {
          if (categoryId) {
            // For specific categories, show proportional jobs based on category size
            // 75 total free jobs distributed proportionally across all categories
            const categoryLimits = {
              '550e8400-e29b-41d4-a716-446655440001': 6,  // Trades & Technical (73 jobs) - 8.2% = 6
              '550e8400-e29b-41d4-a716-446655440002': 12, // Healthcare & Medical (146 jobs) - 8.2% = 12
              '550e8400-e29b-41d4-a716-446655440003': 12, // Technology & IT (149 jobs) - 8.2% = 12
              '642c6bf2-5ddb-4f2e-a047-159d1c590543': 3,  // Business & Finance (4 jobs) - 8.2% = 0.3, round to 3
              '550e8400-e29b-41d4-a716-446655440004': 12, // Business & Management (147 jobs) - 8.2% = 12
              '0e6aa89e-8990-484a-908e-d2c737703994': 6,  // Education & Training (74 jobs) - 8.2% = 6
              '550e8400-e29b-41d4-a716-446655440005': 5,  // Creative & Design (62 jobs) - 8.2% = 5
              '9412465c-4ad1-452d-bc7c-9e0ed615992d': 5,  // Engineering & Technical (61 jobs) - 8.2% = 5
              'e84418ca-4b4d-4bd0-8d63-2496ba0e53b7': 5,  // Sales & Marketing (58 jobs) - 8.2% = 5
              '06790e77-ebc4-4e41-86b8-e212fed5456c': 6   // Service & Hospitality (79 jobs) - 8.2% = 6
            };
            
            const categoryLimit = categoryLimits[categoryId] || 5; // Default to 5 if category not found
            query = query?.limit(categoryLimit);
          } else {
            // For "All Categories", show 72 jobs total (actual sum of proportional distribution)
            query = query?.limit(72);
          }
        }
      } else if (tier) {
        query = query?.eq('tier', tier);
      }
      // Premium users get all occupations - add explicit limit to override Supabase default of 50
      else if (userRole === 'premium' || userRole === 'admin') {
        if (!limit) {
          query = query?.limit(1000); // Get all occupations for premium users
        }
      }

      // Apply smart ordering for better user experience
      if (userRole === 'free' && categoryId) {
        // For free users viewing specific categories, show accessible "down to earth" jobs
        // Order by: lower salary (more accessible), growth outlook, remote friendly, then alphabetically
        query = query?.order('salary_range_max', { ascending: true }) // Lower salaries first (more accessible)
                   ?.order('growth_outlook', { ascending: false }) // Good growth but achievable
                   ?.order('remote_friendly', { ascending: false }) // Modern work options
                   ?.order('title', { ascending: true }); // Consistent alphabetical ordering
      } else {
        // For premium users or "All Categories", use alphabetical ordering
        query = query?.order('title', { ascending: true });
      }
      
      // Apply limit at database level for better performance
      if (limit) {
        query = query?.limit(limit);
      }
      
      if (offset) {
        query = query?.range(offset, offset + (limit || 1000) - 1);
      }

      const { data, error } = await query;

      if (error) {
        console.error('OccupationService: Error searching occupations:', error);
        return { data: null, error };
      }

      // Warm cache for future queries without filters
      try {
        if (!searchTerm && !categoryId) {
          this.occupationsCache = data || [];
          this.occupationsCacheTimestamp = Date.now();
        }
      } catch {}

      console.log('OccupationService: Final result count:', data?.length);
      console.log('OccupationService: Parameters were:', { userRole, categoryId, limit, offset });

      // Track analytics for category searches
      if (categoryId) {
        this.trackCategorySearch(categoryId, searchTerm, data?.length || 0, userRole);
      }

      return { 
        data: data || [], 
        error: null,
        total: data?.length || 0
      };
    } catch (error) {
      console.error('Unexpected error in searchOccupations:', error);
      return { data: null, error };
    }
  }

  /**
   * Get occupation by ID with category information
   */
  static async getOccupationById(occupationId) {
    try {
      if (!occupationId) {
        return { data: null, error: new Error('Occupation ID is required') };
      }

      const { data, error } = await supabase?.from('occupations')?.select(`
          *,
          category:occupation_categories(*)
        `)?.eq('id', occupationId)?.eq('is_active', true)?.single();

      if (error) {
        console.error('Error fetching occupation by ID:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in getOccupationById:', error);
      return { data: null, error };
    }
  }

  /**
   * Get popular occupations by category
   */
  static async getPopularOccupations(categoryId = null, userRole = 'free', limit = 10) {
    try {
      let query = supabase?.from('occupations')?.select(`
          *,
          category:occupation_categories(name, slug, icon, color_class, bg_color_class)
        `)?.eq('is_active', true);

      // Apply category filter
      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      // Apply tier filter based on user role
      if (userRole === 'free') {
        query = query?.eq('tier', 'free');
      }

      const { data, error } = await query?.order('created_at', { ascending: false })?.limit(limit);

      if (error) {
        console.error('Error fetching popular occupations:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Unexpected error in getPopularOccupations:', error);
      return { data: null, error };
    }
  }

  /**
   * Get occupation suggestions based on user profile
   */
  static async getOccupationSuggestions(userId, limit = 5) {
    try {
      if (!userId) {
        return { data: null, error: new Error('User ID is required') };
      }

      // Get user's current occupation and preferences
      const { data: userProfile, error: profileError } = await supabase?.from('user_profiles')?.select(`
          *,
          occupation:occupations(
            category_id,
            tags,
            industry_tags
          )
        `)?.eq('id', userId)?.single();

      if (profileError) {
        console.error('Error fetching user profile for suggestions:', profileError);
        return { data: null, error: profileError };
      }

      let query = supabase?.from('occupations')?.select(`
          *,
          category:occupation_categories(name, slug, icon, color_class, bg_color_class)
        `)?.eq('is_active', true)?.neq('id', userProfile?.occupation_id || ''); // Exclude current occupation

      // Apply tier filter based on user role
      if (userProfile?.role === 'free') {
        query = query?.eq('tier', 'free');
      }

      // If user has an occupation, suggest related ones
      if (userProfile?.occupation?.category_id) {
        query = query?.eq('category_id', userProfile?.occupation?.category_id);
      }

      const { data, error } = await query?.order('salary_range_max', { ascending: false })?.limit(limit);

      if (error) {
        console.error('Error fetching occupation suggestions:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Unexpected error in getOccupationSuggestions:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user's occupation and add to history
   */
  static async updateUserOccupation(userId, occupationId, seniorityLevel = 'entry', companyName = '') {
    try {
      if (!userId || !occupationId) {
        return { data: null, error: new Error('User ID and Occupation ID are required') };
      }

      // Start transaction
      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          occupation_id: occupationId,
          seniority_level: seniorityLevel,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId);

      if (updateError) {
        console.error('Error updating user occupation:', updateError);
        return { data: null, error: updateError };
      }

      // Mark previous occupations as not current
      await supabase?.from('user_occupation_history')?.update({ is_current: false })?.eq('user_id', userId)?.eq('is_current', true);

      // Add new occupation to history
      const { data: historyData, error: historyError } = await supabase?.from('user_occupation_history')?.insert({
          user_id: userId,
          occupation_id: occupationId,
          seniority_level: seniorityLevel,
          start_date: new Date()?.toISOString()?.split('T')?.[0], // Today's date
          is_current: true,
          company_name: companyName
        })?.select()?.single();

      if (historyError) {
        console.error('Error adding occupation to history:', historyError);
        return { data: null, error: historyError };
      }

      return { data: historyData, error: null };
    } catch (error) {
      console.error('Unexpected error in updateUserOccupation:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user's occupation history
   */
  static async getUserOccupationHistory(userId) {
    try {
      if (!userId) {
        return { data: null, error: new Error('User ID is required') };
      }

      const { data, error } = await supabase?.from('user_occupation_history')?.select(`
          *,
          occupation:occupations(
            title,
            description,
            category:occupation_categories(name, icon, color_class)
          )
        `)?.eq('user_id', userId)?.order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching occupation history:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Unexpected error in getUserOccupationHistory:', error);
      return { data: null, error };
    }
  }

  /**
   * Get occupation statistics for analytics
   */
  static async getOccupationStats(occupationId) {
    try {
      if (!occupationId) {
        return { data: null, error: new Error('Occupation ID is required') };
      }

      // Get basic occupation info
      const { data: occupation, error: occupationError } = await this.getOccupationById(occupationId);
      
      if (occupationError) {
        return { data: null, error: occupationError };
      }

      // Get user count for this occupation
      const { count, error: countError } = await supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.eq('occupation_id', occupationId);

      if (countError) {
        console.error('Error getting user count:', countError);
      }

      // Get salary range analysis for category
      const { data: salaryData, error: salaryError } = await supabase?.from('occupations')?.select('salary_range_min, salary_range_max')?.eq('category_id', occupation?.category_id)?.eq('is_active', true);

      let salaryStats = null;
      if (!salaryError && salaryData?.length > 0) {
        const validSalaries = salaryData?.filter(item => item?.salary_range_min && item?.salary_range_max);
        if (validSalaries?.length > 0) {
          const minSalaries = validSalaries?.map(item => item?.salary_range_min);
          const maxSalaries = validSalaries?.map(item => item?.salary_range_max);
          
          salaryStats = {
            category_min: Math.min(...minSalaries),
            category_max: Math.max(...maxSalaries),
            category_avg_min: Math.round(minSalaries?.reduce((a, b) => a + b, 0) / minSalaries?.length),
            category_avg_max: Math.round(maxSalaries?.reduce((a, b) => a + b, 0) / maxSalaries?.length)
          };
        }
      }

      return {
        data: {
          occupation,
          user_count: count || 0,
          salary_stats: salaryStats
        },
        error: null
      };
    } catch (error) {
      console.error('Unexpected error in getOccupationStats:', error);
      return { data: null, error };
    }
  }

  /**
   * Check if user can access premium occupations
   */
  static canAccessPremiumOccupations(userRole) {
    return userRole === 'premium' || userRole === 'admin';
  }

  /**
   * Format occupation for display
   */
  static formatOccupationForDisplay(occupation) {
    if (!occupation) return null;

    return {
      ...occupation,
      formatted_salary: occupation?.salary_range_min && occupation?.salary_range_max 
        ? `$${occupation?.salary_range_min?.toLocaleString()} - $${occupation?.salary_range_max?.toLocaleString()}`
        : 'Salary not specified',
      formatted_tags: occupation?.tags?.slice(0, 3) || [],
      is_premium: occupation?.tier === 'premium'
    };
  }

  /**
   * Clear categories cache (useful for testing or forced refresh)
   */
  static clearCache() {
    this.categoriesCache = null;
    this.cacheTimestamp = null;
  }
}

export default OccupationService;
function occupationService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: occupationService is not implemented yet.', args);
  return null;
}

export { occupationService };